/**
 * FlowPlannerGridConfig.js
 *
 * UI configuration for the Flow Planner grid.
 *
 * PROCESS_HIERARCHY is now built dynamically from admin.json via
 * buildProcessHierarchy() in FlowPlannerModel.js.
 *
 * This file retains:
 *   - DISPLAY_OVERRIDES: UI cosmetic labels (process, group, variant, controller)
 *   - GROUP_SKELETON:    Row ordering + carve-out definitions
 *   - FLOW_PROCESSES / DEFAULT_MULTIPLIER: Algorithm constants
 */

// ─── Constants ──────────────────────────────────────────────────────────────

export const FLOW_PROCESSES = ['INDUCT', 'STOW', 'PICK'];
export const DEFAULT_MULTIPLIER = 1.15;

// ─── Display overrides ──────────────────────────────────────────────────────
// Single map for ALL UI cosmetic labels: group names, controller names,
// process display names, and automation variant names.
// Key = admin.json canonical name; Value = UI label shown in grid.

export const DISPLAY_OVERRIDES = {
  // Group display names (key → UI label)
  WATER_SPIDER: 'Inbound Dock W/S',
  INDUCT_GROUP: 'Induct Group',
  DIVERT: 'Divert',
  STOW: 'Stow',
  PICK_TO_BUFFER: 'Pick to Buffer',
  PROBLEM_SOLVE: 'Sort Problem Solve',
  NON_CON_STOW: 'Non Con Stow',
  OUTBOUND: 'Outbound',

  // Controller name (executionType → UI label)
  MECHANICAL: 'Manual',

  // Process display names (rateName → UI label)
  'Auto Scan Induct Loader': 'ASL Induct Loader',
  'Auto Scan Pusher': 'ASL Pusher',
  Labeler: 'ASML Labeler',
  'Pick Stage & Dispatch Problem Solve': 'Pick Stage Dispatch Problem Solve',
  'Yard Assist and Cart Handling': 'Yard Assist & Cart Handling',

  // Automation variant display names (rateName_executionType → UI label)
  Diverter_MECHANICAL: 'Manual Diverter',
  Diverter_ADTA: 'Auto Divert Straightener',
  'Pick to Buffer_MECHANICAL': 'Pick to Buffer (Manual)',
  'Pick to Buffer_ADTA': 'ADTA Pick to Buffer',
  Stow_MECHANICAL: 'Container Building'
};

// ─── Group skeleton ─────────────────────────────────────────────────────────
// Defines UI row ordering and group composition.
// Children are populated dynamically from admin.json by buildProcessHierarchy().
//
// Fields:
//   key               — Expand-state tracking key
//   processes          — Explicit process name list (for carve-out groups)
//   fromExecutionFlow  — Build controllers/automation from executionFlows[name]
//   sharesAutomation   — Borrow automation split from another executionFlow
//   fromPhase          — Include all processes with this allocationPhase
//   type: 'wip'        — UI-only WIP marker row

export const GROUP_SKELETON = [
  { key: 'WATER_SPIDER', processes: ['Inbound Dock W/S'] },
  { key: 'INDUCT_GROUP', fromExecutionFlow: 'Induct' },
  { key: 'DIVERT', fromExecutionFlow: 'Divert' },
  {
    key: 'PICK_TO_BUFFER',
    processes: ['Pick to Buffer', 'Auto Divert Straightener', 'ADTA Pick to Buffer'],
    sharesAutomation: 'Stow'
  },
  { key: 'STOW_WIP', type: 'wip' },
  { key: 'STOW', fromExecutionFlow: 'Stow' },
  { key: 'PROBLEM_SOLVE', processes: ['Sort Problem Solve'] },
  { key: 'NON_CON_STOW', processes: ['Non-Con Manual Handling', 'Non-Scan Stow'] },
  { key: 'OUTBOUND', fromPhase: 'PICK_AND_STAGE', exclude: ['Pick Stage Problem Solve', 'UTR Dispatch'] }
];

// ─── Volume source → adherence volume series ────────────────────────────────
// Volume actuals use package-status names, not plan process names. Shared by
// Volume Distribution and the Labor Plan grid. Prefer these PROCESS_PATH names
// over the 'Outbound'/'Sortation' aliases, which add planned-only windows.
export const PROCESS_TO_ADHERENCE = { INDUCT: 'Inducted', STOW: 'Stowed', PICK: 'Picked' };

// Labor Plan grid group row → volume-actual series (group rows have no process
// name of their own).
// The only rows that carry a volume actual. Deliberately narrow: the adherence
// series are group totals, so repeating them on sibling or child rows double-reads
// the same number against different planned values.
export const GROUP_VOLUME_ACTUAL = {
  INDUCT_GROUP: PROCESS_TO_ADHERENCE.INDUCT,
  STOW: PROCESS_TO_ADHERENCE.STOW,
  OUTBOUND: PROCESS_TO_ADHERENCE.PICK
};

// New-view group row -> the volume-source process whose distribution it carries.
export const GROUP_VOLUME_SOURCE = { INDUCT_GROUP: 'INDUCT', SORT: 'STOW', OUTBOUND: 'PICK' };

// Leaf rows allowed to match an adherence series of their own name. Narrow on purpose:
// these carry their own series rather than repeating a group total. Row keys are
// volumeAllocationProcess values, so the Manual stow variant is keyed 'Stow' and shown
// as 'Container Building' - both are listed.
export const SELF_MATCHED_VOLUME_ACTUAL_ROWS = new Set(['ADTA Container Building', 'Container Building', 'Stow']);

// New view only: Sort is the parent that Stow sits under, so the stowed total belongs
// on Sort. Classic has no Sort row, hence the separate map.
export const NEW_VIEW_GROUP_VOLUME_ACTUAL = {
  INDUCT_GROUP: PROCESS_TO_ADHERENCE.INDUCT,
  SORT: PROCESS_TO_ADHERENCE.STOW,
  OUTBOUND: PROCESS_TO_ADHERENCE.PICK
};

// ─── Induct controller → adherence volume series ─────────────────────────────
// Induct controller (parent) rows have no adherence process name of their own
// (their key is the executionType), so map each to its per-cycle volume actual.
export const INDUCT_CONTROLLER_VOLUME_ACTUAL = {
  ASL: 'ASL Inducted',
  ASML: 'ASML Inducted',
  MECHANICAL: 'Manual Inducted'
};

// ─── Volume source override ─────────────────────────────────────────────────
// processGroup → laborProcessGroups[].name gives volumeSource for all groups.
// Only DISPATCH_GROUP needs a remap (shares PICK volume).

export const VOLUME_SOURCE_OVERRIDES = { DISPATCH_GROUP: 'PICK' };

// ─── DYNAMIC_V2 key overrides ───────────────────────────────────────────────
// DYNAMIC_V2 requiredHeadcounts uses different keys than STATIC_V3 for some processes.
// Map: STATIC_V3 key → DYNAMIC_V2 key
export const DYNAMIC_V2_KEY_OVERRIDES = {
  Stow: 'Container Building',
  Induct: 'Label'
};
